/**
 * TALEP/TEKLIF ICIN BANKA HESABI ESLEMESI.
 *
 * Bir talep acilabilmesi icin kullanicinin HEM aldigi HEM verdigi para
 * biriminde, AYNI BANKADA hesabi olmali: takas iki bacakli ve ikisi de ayni
 * kurumdan yurur.
 *
 * SUNUCUDA DA UYGULANIYOR (2026-09-23, denetim H-2): bacak hesabi
 * services/dovizhub-currency-management/src/modules/task-legs.js (customerLegs /
 * exchangeLegs) ve kontrolu app-service.js postTask (ACCOUNT_LEG_MISMATCH,
 * ACCOUNT_COUNTER_LEG_MISSING) ile postTaskOffer (ACCOUNT_LEG_MISMATCH). Oncesinde
 * musteri tarafinda YALNIZ bu istemci kopyasi vardi (bu yorum o zaman yanlisti).
 * Buradaki kopya kullaniciyi calismayacak bir yola sokmamak icin; iki taraf
 * degisirse BIRLIKTE degismeli.
 *
 * Kaynak: dovizhub-mobile/src/shared/utils/account.ts
 * (findAccountsForCustomer / findAccountsForExchange).
 *
 * NOT: hesap YONETIMI ekranlari bu dosyanin isi degil (src/features/accounts).
 * Burada yalniz "hangi hesap bu isleme uygun" sorusu cevaplaniyor.
 */

export type BankAccount = {
  /** Banka kimligi — iki bacagin AYNI kurumda olmasi bununla dogrulanir. */
  accountCode: string;
  accountCurrency: string;
  accountLabel?: string;
  accountName?: string;
  accountIbanCode?: string;
};

/** Islemin iki bacagi: gelen ve giden para birimi. */
type Legs = { incoming: string; outgoing: string };

/**
 * Musteri tarafinin bacaklari.
 *   ALIS  : `currency` gelir, TRY gider.
 *   SATIS : TRY gelir, `currency` gider.
 * EUR/USD'de TRY yerine ciftin oteki bacagi kullanilir.
 */
const customerLegs = (currency: string, isBuying: boolean): Legs => {
  if (currency === 'EUR/USD') {
    return isBuying ? { incoming: 'EUR', outgoing: 'USD' } : { incoming: 'USD', outgoing: 'EUR' };
  }
  return isBuying ? { incoming: currency, outgoing: 'TRY' } : { incoming: 'TRY', outgoing: currency };
};

/** Buro tarafi musterinin TAM TERSIDIR — takasin oteki ucu. */
const exchangeLegs = (currency: string, isBuying: boolean): Legs => {
  if (currency === 'EUR/USD') {
    return isBuying ? { incoming: 'USD', outgoing: 'EUR' } : { incoming: 'EUR', outgoing: 'USD' };
  }
  return isBuying ? { incoming: 'TRY', outgoing: currency } : { incoming: currency, outgoing: 'TRY' };
};

/** Ayni bankada `outgoing` bacagi da var mi? */
const hasCounterLeg = (accounts: readonly BankAccount[], accountCode: string, currency: string) =>
  accounts.some((other) => other.accountCode === accountCode && other.accountCurrency === currency);

/**
 * Musterinin bu islem icin kullanabilecegi hesaplar. Bos donerse talep
 * ACILAMAZ ve kullaniciya eksik hesabin ne oldugu soylenir.
 */
export const findAccountsForCustomer = (
  accounts: readonly BankAccount[],
  currency: string,
  isBuying: boolean,
): BankAccount[] => {
  const legs = customerLegs(currency, isBuying);
  return accounts.filter(
    (account) =>
      account.accountCurrency === legs.incoming &&
      hasCounterLeg(accounts, account.accountCode, legs.outgoing),
  );
};

/**
 * Buronun teklif verebilmek icin kullanabilecegi hesaplar.
 *
 * `accountCode` MUSTERININ sectigi bankadir: takas ayni kurumdan yurudugu icin
 * buronun da o bankada hesabi olmak zorunda. Kod bos ise eslesme yapilamaz.
 */
export const findAccountsForExchange = (
  accounts: readonly BankAccount[],
  currency: string,
  isBuying: boolean,
  accountCode: string | null | undefined,
): BankAccount[] => {
  if (!accountCode || !currency) return [];
  const legs = exchangeLegs(currency, isBuying);
  return accounts.filter(
    (account) =>
      account.accountCode === accountCode &&
      account.accountCurrency === legs.incoming &&
      hasCounterLeg(accounts, accountCode, legs.outgoing),
  );
};
